import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useBooking } from "@/context/BookingContext";
import { createConsultation, getSeason } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", package_interest: "", message: "" };

export const ConsultationModal = () => {
  const { open, setOpen, presetPackage } = useBooking();
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, package_interest: presetPackage || "" });
      setDate(null);
      setSeason(null);
      setDone(false);
    }
  }, [open, presetPackage]);

  useEffect(() => {
    if (!date) {
      setSeason(null);
      return;
    }
    getSeason(format(date, "yyyy-MM-dd")).then(setSeason).catch(() => setSeason(null));
  }, [date]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please pick a preferred travel date.");
      return;
    }
    setLoading(true);
    try {
      await createConsultation({
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
        package_interest: form.package_interest || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Consultation requested — our team will call you soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-h-[90vh] max-w-md overflow-y-auto rounded-none border-black/10 bg-white p-6 sm:max-w-lg sm:p-8"
        data-testid="consultation-modal"
      >
        {done ? (
          <div className="py-10 text-center" data-testid="consultation-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold" />
            <h3 className="mt-6 font-display text-2xl text-ink">Consultation Requested</h3>
            <p className="mt-3 text-sm text-black/55">
              Thank you, {form.name || "traveller"}. Our team will reach out to align your
              dates with the right season for the crystal-clear river.
            </p>
            <Button
              data-testid="consultation-close-btn"
              onClick={() => setOpen(false)}
              className="mt-8 h-12 w-full rounded-none bg-ink text-white hover:bg-ink/90"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <p className="eyebrow text-gold">Private Consultation</p>
              <DialogTitle className="font-display text-2xl text-ink">
                Schedule Your 1-to-1 Call
              </DialogTitle>
              <DialogDescription className="text-black/55">
                Share a few details and your preferred travel date. We will check seasonal
                alignment before your first call.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="mt-4 space-y-4" data-testid="consultation-form">
              <div className="space-y-2">
                <Label htmlFor="c-name">Full Name</Label>
                <Input id="c-name" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="consult-name-input" className="rounded-none" />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="c-email">Email</Label>
                  <Input id="c-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="consult-email-input" className="rounded-none" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" required value={form.phone} onChange={set("phone")} placeholder="+91 …" data-testid="consult-phone-input" className="rounded-none" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Preferred Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="consult-date-trigger"
                        className={cn("w-full justify-start rounded-none font-normal", !date && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        defaultMonth={date || new Date()}
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return d < today;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Package Interest</Label>
                  <Select value={form.package_interest} onValueChange={(v) => setForm((f) => ({ ...f, package_interest: v }))}>
                    <SelectTrigger data-testid="consult-package-select" className="rounded-none">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Private Family Travel">Private Family Travel</SelectItem>
                      <SelectItem value="The Micro-Group Experience">Micro-Group Experience</SelectItem>
                      <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {season && (
                <div
                  className={`border-l-2 px-4 py-3 text-sm ${season.ideal ? "border-gold bg-gold/10" : "border-black/30 bg-black/5"}`}
                  data-testid="season-note"
                >
                  <p className={`font-semibold ${season.ideal ? "text-gold" : "text-ink"}`}>{season.season}</p>
                  <p className="mt-1 text-black/60">{season.note}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="c-msg">Anything we should know? (optional)</Label>
                <Input id="c-msg" value={form.message} onChange={set("message")} placeholder="Family size, elders, dietary needs…" data-testid="consult-message-input" className="rounded-none" />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="consult-submit-btn"
                className="group mt-2 h-12 w-full rounded-none bg-gold text-sm font-bold uppercase tracking-[0.1em] text-ink hover:bg-gold-hover"
              >
                {loading ? "Submitting…" : "Request Consultation"}
                <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
